import React from 'react';
import { DollarSign } from 'lucide-react';
import type { ClinicService, ValidationErrors } from '../../../types/wizard';
import '../steps/PricingStep.css';

interface PricingFormProps {
  services: ClinicService[];
  onUpdateFee: (serviceProcedureId: number, fee: number | undefined) => void;
  errors: ValidationErrors;
}

const PricingForm: React.FC<Readonly<PricingFormProps>> = ({ services, onUpdateFee, errors }) => {
  const handleFeeChange = (serviceProcedureId: number, value: string) => {
    if (value === '') {
      onUpdateFee(serviceProcedureId, undefined);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onUpdateFee(serviceProcedureId, numValue);
    }
  };

  if (services.length === 0) {
    return (
      <div className="skip-message">
        <p>No services were selected.</p>
        <p className="skip-hint">Add services to set pricing.</p>
      </div>
    );
  }

  const servicesWithFees = services.filter((s) => s.fee !== undefined && s.fee > 0);
  const totalFees = servicesWithFees.reduce((sum, s) => sum + (s.fee || 0), 0);

  return (
    <>
      <div className="pricing-summary">
        <div className="summary-item">
          <span className="summary-label">Services Selected</span>
          <span className="summary-value">{services.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">With Fees Set</span>
          <span className="summary-value">{servicesWithFees.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total Fees</span>
          <span className="summary-value total">${totalFees.toFixed(2)}</span>
        </div>
      </div>

      <div className="pricing-table-container">
        <table className="pricing-table">
          <thead>
            <tr>
              <th className="col-name">Service Name</th>
              <th className="col-code">Code</th>
              <th className="col-fee">Fee ($)</th>
            </tr>
          </thead>
          <tbody>
            {services.map((service, index) => {
              const errorKey = `service${index}_fee`;
              const hasError = !!errors[errorKey];

              return (
                <tr key={service.serviceProcedureId} className={hasError ? 'has-error' : ''}>
                  <td className="col-name">
                    <span className="service-name">{service.name}</span>
                  </td>
                  <td className="col-code">
                    <span className="service-code">{service.code}</span>
                  </td>
                  <td className="col-fee">
                    <div className="fee-input-wrapper">
                      <DollarSign size={16} className="fee-icon" />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={service.fee !== undefined ? service.fee : ''}
                        onChange={(e) => handleFeeChange(service.serviceProcedureId, e.target.value)}
                        className={hasError ? 'error' : ''}
                        aria-label={`${service.name} fee`}
                      />
                    </div>
                    {hasError && (
                      <div className="fee-error">{errors[errorKey]}</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pricing-note">
        <p>
          <strong>Tip:</strong> You can leave fees blank for now and set them later from the clinic's fee management page.
        </p>
      </div>
    </>
  );
};

export default PricingForm;
